// AHA-47349-presenter-can-create-new-slide-type
// Developer slide types: fetch the slide types a user has created from the
// backend's public user-manifests endpoint and normalise them into the same
// shape /api/slide-types uses, so they can be appended to the marketplace
// catalogue (and listed on the Developer page).
import Vue from 'vue'
import Config from '@/config'

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '')

// Bounded fetch: this runs on the cached picker-open fast path (awaited by every
// caller of fetchMarketplaceSlideTypes), so a slow/unreachable backend must never
// stall it indefinitely. Abort each request after a hard timeout; on abort fetch
// rejects and the caller falls back to [].
const FETCH_TIMEOUT_MS = 5000
const fetchWithTimeout = async (url, options) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  try {
    return await fetch(url, controller ? { ...options, signal: controller.signal } : options)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// Make a (possibly relative) URL absolute against the slide type's own baseUrl.
const absolutize = (base, url) => {
  const value = String(url || '')
  if (!value) return value
  if (/^https?:\/\//i.test(value)) return value
  return `${trimTrailingSlash(base)}${value.startsWith('/') ? '' : '/'}${value}`
}

// Normalise one user-manifest slide type into the SAME shape as an /api/slide-types
// catalogue entry, so every downstream consumer (picker, canvas/editor/audience
// resolution) treats it exactly like a first-party plugin slide type. The API entry
// mirrors the catalogue feed: a nested `ahaConfig` (URLs already absolute) plus the
// top-level entry (URLs relative). We flatten it the same way the marketplace loader
// does — spread `ahaConfig`, then the top-level entry so its keys win — then force
// the canonical fields. Key points:
//   • `type` IS the `pageId` — the stable per-deploy identifier a slide stores as
//     `slide.type`. embedApp resolves these by the `isDeveloperSlideType` marker,
//     and its marketplace branch explicitly excludes developer entries, so a
//     spread-in `source: 'market'` can never route one through the marketplace branch.
//   • ONLY the plugin's own iframe entries (canvas/editor/audience/handler) plus a
//     path-shaped `icon` are absolutized against the entry's `baseUrl`. A
//     FontAwesome-class `icon` (`fas fa-heart`, no `/`) and `previewUrl` (a
//     presenter-served asset) are kept verbatim.
//   • FAIL CLOSED: without an absolute base the iframe URL stays relative and would
//     resolve against the presenter origin (loading the app itself inside the
//     plugin iframe). Drop such entries, matching the old resolver's "no base => []".
//   • `plugin: true`; `setting` and the rest of the catalogue fields kept from the flatten.
const isAbsoluteUrl = (url) => /^https?:\/\//i.test(String(url || ''))
const normalize = (entry) => {
  if (!entry || typeof entry.pageId !== 'string' || !entry.pageId) return null
  const cfg = (entry.ahaConfig && typeof entry.ahaConfig === 'object') ? entry.ahaConfig : {}
  const { ahaConfig, ...topLevel } = entry
  const merged = { ...cfg, ...topLevel }
  const abs = (url) => absolutize(entry.baseUrl || '', url)
  const canvasUrl = abs(merged.canvasUrl)
  if (!isAbsoluteUrl(canvasUrl)) return null
  // Absolutize a path-shaped icon (`/icon.svg`) against the plugin host; keep a
  // FontAwesome class (no `/`) as-is.
  const icon = (typeof merged.icon === 'string' && merged.icon.includes('/')) ? abs(merged.icon) : merged.icon
  return {
    ...merged,
    type: entry.pageId,
    slug: entry.slug || cfg.slug || '',
    plugin: true,
    isDeveloperSlideType: true,
    canvasUrl,
    editorUrl: abs(merged.editorUrl),
    audienceUrl: abs(merged.audienceUrl),
    handlerUrl: merged.handlerUrl ? abs(merged.handlerUrl) : (merged.handlerUrl ?? null),
    icon,
    setting: (merged.setting && typeof merged.setting === 'object') ? merged.setting : {},
    baseUrl: entry.baseUrl || '',
    pageId: entry.pageId,
  }
}

// GET a public slide-type list endpoint and normalise it. By default it never
// rejects: any failure (network, timeout, non-OK, bad JSON, non-array payload)
// yields []. `strict` rejects on those instead, for a caller that REPLACES what it
// has and must tell "failed" from "genuinely empty". `fresh` gets past the
// endpoint's `public, max-age=60`, used right after a publish / unpublish: CloudFront
// caches it too and ignores a request `Cache-Control`, but keys on the query string,
// so a one-off `_` param forces a miss at the CDN and `no-cache` at the browser.
const fetchSlideTypeList = async (path, { fresh = false, strict = false } = {}) => {
  let data
  try {
    const url = `${trimTrailingSlash(Config.baseUrl)}${path}${fresh ? `?_=${Date.now()}` : ''}`
    const res = await fetchWithTimeout(url, {
      headers: { accept: 'application/json' },
      ...(fresh ? { cache: 'no-cache' } : {}),
    })
    if (!res.ok) throw new Error(`${path} responded ${res.status}`)
    data = await res.json()
  } catch (e) {
    if (strict) throw e
    return []
  }
  // Guard a non-array `slideTypes` (object/string): `entries.map` would throw here,
  // OUTSIDE the fetch try/catch, and reject instead of returning [] as documented.
  const raw = Array.isArray(data) ? data : (data && data.slideTypes)
  if (!Array.isArray(raw)) {
    if (strict) throw new Error(`${path} returned no slideTypes list`)
    return []
  }
  return raw.map(normalize).filter(Boolean)
}

// The user's own drafts are named "Dev: <name>" in the catalogue so the slide-type
// picker tells them apart from built-ins. Published types are open to every
// presenter and keep the creator's name as-is. Done on the data, not per card, so
// every picker surface, search and Recent see one consistent name; the Developer
// page's owner list keeps the plain name. Idempotent.
const DEV_PREFIX = 'Dev: '
const withDevPrefix = (item) => {
  const name = item.name || ''
  return name.startsWith(DEV_PREFIX) ? item : { ...item, name: `${DEV_PREFIX}${name}` }
}

/**
 * Returns the developer slide types a user has created, or [] when there is
 * nothing to add / the request fails. Read from the backend's public endpoint
 * (no auth — the userId is in the path), so it is safe to call from any build.
 */
export const fetchDeveloperSlideTypes = async ({ userId, fresh = false, strict = false }) => {
  if (!userId) return []
  const items = await fetchSlideTypeList(`/api/slide/user-manifests/public/${encodeURIComponent(userId)}`, { fresh, strict })
  return items.map(withDevPrefix)
}

// A published slide type is served from its own snapshot page, whose pageId the
// backend keeps for the type's whole life (republish and unpublish/publish
// included). Like a draft, the entry's `type` IS that pageId, and its pinKey is
// re-keyed on it: the inherited draft pinKey would collide with the draft's card.
// Its category prefix is kept. `publishedKey` (the source draft's id) is kept on
// the entry so slides created while the type was keyed by it still resolve.
const publishedPinKey = (item) => {
  const prefix = typeof item.pinKey === 'string' && item.pinKey.includes('/')
    ? item.pinKey.slice(0, item.pinKey.lastIndexOf('/'))
    : 'published'
  return `${prefix}/${item.pageId}`
}

/**
 * Returns every published slide type (all creators) from the backend's public
 * published-manifests endpoint, in the same shape as fetchDeveloperSlideTypes so
 * the two lists can be concatenated. `type` is the snapshot's stable pageId;
 * `isPublishedSlideType` marks them. Entries without a `publishedKey` are
 * dropped. [] on failure.
 */
export const fetchPublishedSlideTypes = async ({ fresh = false, strict = false } = {}) => {
  const items = await fetchSlideTypeList('/api/slide/published-manifests', { fresh, strict })
  return items
    .filter((item) => typeof item.publishedKey === 'string' && item.publishedKey)
    .map((item) => ({
      ...item,
      pinKey: publishedPinKey(item),
      isPublishedSlideType: true,
    }))
}

// Owner-side (authenticated) calls for the Developer page. The owner list carries
// each draft's publish state (`publishedPageId`/`publishedAt`); publishing
// snapshots the draft into a NEW page, and unpublishing deletes that snapshot by
// ITS page id — never the draft's.
let resource = null

export const actions = {
  listMine: {
    method: 'GET',
    url: `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests`,
  },
  publish: {
    method: 'POST',
    url: `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests/{pageId}/publish`,
  },
  unpublish: {
    method: 'DELETE',
    url: `${trimTrailingSlash(Config.baseUrl)}/api/slide/published-manifests/{pageId}`,
  },
  getMine: {
    method: 'GET',
    url: `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests/{pageId}`,
  },
  share: {
    method: 'POST',
    url: `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests/{pageId}/share`,
  },
  accept: {
    method: 'POST',
    url: `${trimTrailingSlash(Config.baseUrl)}/api/slide/user-manifests/accept`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

/**
 * Returns the slide types the signed-in user created (shared-with-me entries are
 * left out: only the owner can publish), normalised like fetchDeveloperSlideTypes
 * plus `publishedPageId`/`publishedAt`. Rejects on a failed request.
 */
export const fetchMySlideTypes = async () => {
  if (!resource) initResource()
  const res = await resource.listMine()
  const raw = res.body && res.body.slideTypes
  const entries = Array.isArray(raw) ? raw : []
  return entries
    .filter((entry) => entry && !entry.shared)
    .map(normalize)
    .filter(Boolean)
    .map((item) => ({
      ...item,
      publishedPageId: item.publishedPageId || null,
      publishedAt: item.publishedAt || null,
    }))
}

/** Publishes (or re-publishes) a draft; resolves with the published snapshot entry. */
export const publishSlideType = async ({ pageId }) => {
  if (!resource) initResource()
  const res = await resource.publish({ pageId }, {})
  return res.body.slideType
}

/** Unpublishes by the SNAPSHOT's page id (the draft's `publishedPageId`). */
export const unpublishSlideType = async ({ publishedPageId }) => {
  if (!resource) initResource()
  await resource.unpublish({ pageId: publishedPageId })
}

/**
 * Returns one of the signed-in user's draft slide types (their own or one added
 * from a share link) straight from the authenticated endpoint, normalised like
 * fetchDeveloperSlideTypes — for right after a share link, when the public list
 * may still be cached without it. Rejects on a failed request.
 */
export const fetchMySlideType = async ({ pageId }) => {
  if (!resource) initResource()
  const res = await resource.getMine({ pageId })
  const item = normalize(res.body && res.body.slideType)
  return item ? withDevPrefix(item) : null
}

/**
 * Turns on the share link of one of the user's drafts; resolves with its
 * `{ shareCode, acceptPath }`. The code is stable, so calling again is safe.
 */
export const shareSlideType = async ({ pageId }) => {
  if (!resource) initResource()
  const res = await resource.share({ pageId }, {})
  return res.body.share
}

/**
 * Adds the slide type behind a share code to the signed-in user's slide types and
 * resolves with it, normalised like fetchDeveloperSlideTypes. It stays the owner's
 * slide type: read-only here, and it follows the owner's edits. Accepting twice is
 * a no-op. Rejects with the response (404 bad link, 409 own slide type).
 */
export const acceptSharedSlideType = async ({ code }) => {
  if (!resource) initResource()
  const res = await resource.accept({}, { code })
  const item = normalize(res.body && res.body.slideType)
  return item ? withDevPrefix(item) : null
}

export default {
  fetchDeveloperSlideTypes,
  fetchPublishedSlideTypes,
  fetchMySlideTypes,
  publishSlideType,
  unpublishSlideType,
  fetchMySlideType,
  shareSlideType,
  acceptSharedSlideType,
  initResource,
}
